import React, { useState, useEffect } from "react";
import { 
  addDays, 
  subDays, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfMonth, 
  subMonths, 
  subWeeks, 
  startOfQuarter, 
  endOfQuarter, 
  subQuarters, 
  format,
  isSameDay
} from "date-fns";

import { useNavigate } from "react-router-dom";
import { ChevronLeft, UserMinus, Search, Calendar as CalendarIcon, Eye, Loader2, Download } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { api } from "../../utils/api";
import { Calendar as CalendarUI } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Clock, Info, AlertCircle, Calendar, FileText, Paperclip, History as HistoryIcon, ChevronRight } from "lucide-react";
import { 
  Pagination, 
  PaginationContent, 
  PaginationEllipsis, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious 
} from "../../components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { LeaveHandoverSummary, type LeaveHandoverSummaryData } from "../../components/LeaveHandoverSummary";



type StatusType = "Present" | "Annual Leave" | "Personal Leave" | string;

interface AttendanceRecord {
  id: string;
  date: string;
  employee: { name: string; initials: string };
  department: string;
  status: StatusType;
  reportedBy?: string | null;
  reportedAt?: string | null;
  details?: {
    type: 'present' | 'leave' | 'absence';
    startDate?: string;
    endDate?: string;
    leaveType?: string;
    reason?: string;
    status?: string;
    hours?: number;
    notes?: string;
    reportedDate?: string;
    attachments?: { name: string; url: string }[];
    handover?: LeaveHandoverSummaryData | null;
  };
}

const getStatusBadge = (status: StatusType) => {
  switch (status) {
    case "Present":
      return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-100 font-bold">Present</Badge>;
    case "Annual Leave":
      return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-100 font-bold">Annual Leave</Badge>;
    case "Personal Leave":
      return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-100 font-bold">Personal Leave</Badge>;
    case "Unpaid Leave":
      return <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-100 font-bold">Unpaid Leave</Badge>;
    default:
      return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-100 font-bold">{status}</Badge>;
  }
};

const AttendanceHistory = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<{ from: Date | undefined; to?: Date | undefined }>({
    from: startOfWeek(new Date(), { weekStartsOn: 1 }),
    to: endOfWeek(new Date(), { weekStartsOn: 1 })
  });

  const [activePreset, setActivePreset] = useState<string>("This Week");
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("All");
  const RECORDS_PER_PAGE = 15;



  
  const [selectedRecord, setSelectedRecord] = useState<AttendanceRecord | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, [dateRange]);

  const handlePresetChange = (preset: string) => {
    setActivePreset(preset);
    const now = new Date();
    let from: Date | undefined;
    let to: Date | undefined;

    switch (preset) {
      case "This Week":
        from = startOfWeek(now, { weekStartsOn: 1 });
        to = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case "Last Week":
        const lastWeek = subWeeks(now, 1);
        from = startOfWeek(lastWeek, { weekStartsOn: 1 });
        to = endOfWeek(lastWeek, { weekStartsOn: 1 });
        break;
      case "Last Month":
        const lastMonth = subMonths(now, 1);
        from = startOfMonth(lastMonth);
        to = endOfMonth(lastMonth);
        break;
      case "Last Quarter":
        const lastQuarter = subQuarters(now, 1);
        from = startOfQuarter(lastQuarter);
        to = endOfQuarter(lastQuarter);
        break;
      case "Custom":
        // Keep current range, user will pick
        return;
      default:
        return;
    }
    setDateRange({ from, to });
    setCurrentPage(1);
  };


  const fetchHistory = async () => {
    if (!dateRange.from || !dateRange.to) return;
    
    setLoading(true);
    try {
      let url = '/api/attendance/team-history';
      const startStr = format(dateRange.from, 'yyyy-MM-dd');
      const endStr = format(dateRange.to, 'yyyy-MM-dd');
      url += `?startDate=${startStr}&endDate=${endStr}`;
      
      const res = await api.get(url);
      if (res.success) {
        setRecords(res.data);
      }
    } catch (error) {
      console.error("Error fetching attendance history:", error);
    } finally {
      setLoading(false);
    }
  };


  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };


  const filteredRecords = records.filter(record => {
    const matchesSearch = 
      record.employee.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      record.department.toLowerCase().includes(searchQuery.toLowerCase()) ||
      record.status.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (statusFilter === "Present") {
      return matchesSearch && record.status === "Present";
    }
    if (statusFilter === "On Leave") {
      return matchesSearch && record.status !== "Present";
    }
    return matchesSearch;
  });


  const totalPages = Math.ceil(filteredRecords.length / RECORDS_PER_PAGE);
  const paginatedRecords = filteredRecords.slice(
    (currentPage - 1) * RECORDS_PER_PAGE,
    currentPage * RECORDS_PER_PAGE
  );


  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleExport = () => {
    if (filteredRecords.length === 0) return;

    const headers = ["Date", "Employee", "Department", "Status", "Reported By", "Reported At"];
    const csvContent = [
      headers.join(","),
      ...filteredRecords.map(r => [
        formatDate(r.date),
        `"${r.employee.name}"`,
        `"${r.department}"`,
        `"${r.status}"`,
        `"${r.reportedBy || 'N/A'}"`,
        `"${r.reportedAt ? formatDateTime(r.reportedAt) : 'N/A'}"`
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `attendance_history_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openDetailModal = (record: AttendanceRecord) => {
    setSelectedRecord(record);
    setIsDetailModalOpen(true);
  };

  return (
    <div className="max-w-[1200px] mx-auto pb-20 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            className="rounded-xl border-gray-100 bg-white hover:bg-gray-50 shrink-0"
            onClick={() => navigate("/attendance")}
          >
            <ChevronLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Attendance History & Management</h1>
            <p className="text-gray-500 font-medium text-sm">View your team's attendance history and report absences</p>
          </div>
        </div>
        <Button 
          className="bg-black text-white hover:bg-gray-800 flex items-center gap-2 rounded-xl h-11 px-6"
          onClick={() => navigate('/attendance/report-absence')}
        >
          <UserMinus className="h-4 w-4" />
          Report Absentee
        </Button>
      </div>

      <div className="space-y-6">
        {/* Filters */}
        <div className="bg-white p-6 rounded-[24px] border border-gray-100 shadow-sm space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="font-bold text-gray-900 text-lg">Filters</h3>
            <Button 
              variant="outline" 
              onClick={handleExport}
              className="rounded-xl border-gray-200 h-10 px-4 flex items-center gap-2 bg-white text-gray-700 font-bold hover:bg-gray-50"
              disabled={filteredRecords.length === 0}
            >
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          </div>

          <div className="flex flex-col space-y-6">
            <div className="flex flex-wrap gap-2">
              {["This Week", "Last Week", "Last Month", "Last Quarter", "Custom"].map((preset) => (
                <Button
                  key={preset}
                  variant={activePreset === preset ? "default" : "outline"}
                  onClick={() => handlePresetChange(preset)}
                  className={`rounded-xl h-9 px-4 text-xs font-bold transition-all ${
                    activePreset === preset 
                      ? "bg-black text-white hover:bg-gray-800" 
                      : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-900 shadow-sm"
                  }`}
                >
                  {preset}
                </Button>
              ))}
            </div>

            <div className="flex flex-col lg:flex-row items-end gap-4">
              <div className="w-full lg:flex-1 space-y-2">
                <label className="text-xs font-bold text-gray-900 tracking-wide ml-1">Search Employee</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
                    <Search className="h-4 w-4" />
                  </div>
                  <Input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Search name, department or status..."
                    className="rounded-xl border-gray-200 bg-gray-50/50 h-11 focus:bg-white transition-all pl-10 text-sm font-medium"
                  />
                </div>
              </div>

              <div className="w-full md:w-[200px] space-y-2">
                <label className="text-xs font-bold text-gray-900 tracking-wide ml-1">Status</label>
                <Select value={statusFilter} onValueChange={(val) => { setStatusFilter(val); setCurrentPage(1); }}>
                  <SelectTrigger className="rounded-xl border-gray-200 h-11 font-bold bg-gray-50/50">
                    <SelectValue placeholder="Select Status" />
                  </SelectTrigger>
                  <SelectContent className="rounded-2xl border-gray-100 shadow-xl">
                    <SelectItem value="All" className="font-bold">All Statuses</SelectItem>
                    <SelectItem value="Present" className="font-bold">Present</SelectItem>
                    <SelectItem value="On Leave" className="font-bold">On Leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col space-y-2 min-w-[280px]">
                <label className="text-xs font-bold text-gray-900 tracking-wide ml-1">Date Range</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={`w-full rounded-xl border-gray-200 h-11 px-4 justify-start text-left font-bold bg-gray-50/50 ${!dateRange.from ? "text-muted-foreground" : ""}`}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-gray-400" />
                      {dateRange?.from ? (
                        dateRange.to ? (
                          <>
                            {format(dateRange.from, "LLL dd, y") + " - " + format(dateRange.to, "LLL dd, y")}
                          </>
                        ) : (
                          format(dateRange.from, "LLL dd, y")
                        )
                      ) : (
                        <span>Pick a date range</span>
                      )}
                    </Button>
                  </PopoverTrigger>

                  <PopoverContent className="w-auto p-0 rounded-2xl border-gray-100 shadow-xl" align="start">
                    <CalendarUI
                      initialFocus
                      mode="range"
                      defaultMonth={dateRange?.from}
                      selected={dateRange}
                      onSelect={(range) => {
                        setDateRange(range || { from: undefined, to: undefined });
                        setActivePreset("Custom");
                      }}
                      numberOfMonths={2}
                      className="rounded-2xl"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

        </div>

        {/* Attendance Records */}
        <div className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden text-gray-900">
          <div className="p-6 border-b border-gray-100 flex justify-between items-center">
            <div>
              <h3 className="font-bold text-lg">Attendance Records</h3>
              <p className="text-gray-500 text-sm font-medium">History for your team members</p>
            </div>
            {loading && <Loader2 className="h-5 w-5 animate-spin text-gray-400" />}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-900 font-bold bg-white border-b border-gray-100 uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-4">Date</th>
                  <th className="px-6 py-4">Employee</th>
                  <th className="px-6 py-4">Department</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4">Reported By</th>
                  <th className="px-6 py-4">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                        <p className="font-medium italic">Loading attendance log...</p>
                      </div>
                    </td>
                  </tr>
                ) : filteredRecords.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500 italic">
                      No records found matching your current filters.
                    </td>
                  </tr>
                ) : (

                  paginatedRecords.map((record) => (

                    <tr key={record.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4 text-gray-900 font-bold whitespace-nowrap">
                        {formatDate(record.date)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8 text-xs font-medium border border-gray-100 shadow-sm">
                            <AvatarFallback className="bg-gray-50 text-gray-600">
                              {record.employee.initials}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-gray-900 font-bold">{record.employee.name}</span>
                        </div>
                      </td>

                      <td className="px-6 py-4 text-gray-500 font-medium whitespace-nowrap">
                        {record.department}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(record.status)}
                      </td>
                      <td className="px-6 py-4">
                        {record.reportedBy ? (
                          <div className="flex flex-col">
                            <span className="text-gray-900 font-bold text-xs">{record.reportedBy}</span>
                            <span className="text-[10px] text-gray-400 font-medium whitespace-nowrap">
                              {record.reportedAt ? formatDateTime(record.reportedAt) : ''}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs italic">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <Button 
                          variant="ghost" 
                          title="View Details" 
                          className="h-8 px-2 text-gray-700 font-bold hover:text-gray-900 flex items-center gap-2"
                          onClick={() => openDetailModal(record)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-6 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-gray-500 font-medium">
                Showing <span className="font-bold text-gray-900">{((currentPage - 1) * RECORDS_PER_PAGE) + 1}</span> to <span className="font-bold text-gray-900">{Math.min(currentPage * RECORDS_PER_PAGE, filteredRecords.length)}</span> of <span className="font-bold text-gray-900">{filteredRecords.length}</span> records
              </p>
              <Pagination className="mx-0 w-auto">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      className={`h-9 rounded-xl border-gray-200 cursor-pointer ${currentPage === 1 ? "opacity-50 pointer-events-none" : "hover:bg-gray-50 text-gray-700 font-bold"}`}
                    />
                  </PaginationItem>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      // Show first, last, and pages around current page
                      return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                    })
                    .map((page, index, array) => {
                      const showEllipsis = index > 0 && page - array[index - 1] > 1;
                      return (
                        <React.Fragment key={page}>
                          {showEllipsis && (
                            <PaginationItem>
                              <PaginationEllipsis />
                            </PaginationItem>
                          )}
                          <PaginationItem>
                            <PaginationLink
                              isActive={currentPage === page}
                              onClick={() => setCurrentPage(page)}
                              className={`h-9 w-9 rounded-xl border-gray-200 cursor-pointer text-xs font-bold ${
                                currentPage === page 
                                  ? "bg-black text-white border-black" 
                                  : "bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 shadow-sm"
                              }`}
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        </React.Fragment>
                      );
                    })}

                  <PaginationItem>
                    <PaginationNext 
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      className={`h-9 rounded-xl border-gray-200 cursor-pointer ${currentPage === totalPages ? "opacity-50 pointer-events-none" : "hover:bg-gray-50 text-gray-700 font-bold"}`}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>

      </div>

      {/* Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="sm:max-w-[550px] rounded-[32px] border-none shadow-2xl p-0 overflow-hidden">
          <DialogHeader className="p-8 pb-4">
            <DialogTitle className="text-2xl font-bold text-gray-900">Attendance Detail</DialogTitle>
          </DialogHeader>
          
          {selectedRecord && (
            <div className="p-8 pt-0 space-y-6">
              {/* Employee Info Header */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl border border-gray-100">
                <Avatar className="h-12 w-12 border-2 border-white shadow-sm">
                  <AvatarFallback className="bg-white text-gray-600 font-bold italic">
                    {selectedRecord.employee.initials}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h4 className="font-bold text-gray-900">{selectedRecord.employee.name}</h4>
                  <p className="text-gray-500 text-xs font-medium uppercase tracking-wider">{selectedRecord.department}</p>
                </div>
                <div className="ml-auto">
                  {getStatusBadge(selectedRecord.status)}
                </div>
              </div>

              {/* Status Specific Details */}
              <div className="space-y-5">
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <Calendar className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Date</p>
                    <p className="text-sm font-bold text-gray-900">{formatDate(selectedRecord.date)}</p>
                  </div>
                </div>

                {selectedRecord.details?.type === 'leave' && (
                  <>
                    <div className="flex items-center gap-3 text-gray-600">
                      <div className="p-2 bg-purple-50 rounded-lg">
                        <Clock className="h-4 w-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Leave Duration</p>
                        <p className="text-sm font-bold text-gray-900">
                          {formatDate(selectedRecord.details.startDate!)} - {formatDate(selectedRecord.details.endDate!)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50/50 rounded-2xl p-4 border border-gray-100 space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-50 rounded-lg">
                            <CalendarIcon className="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Leave Duration</p>
                            <p className="text-sm font-bold text-gray-900">
                              {parseFloat(String(selectedRecord.details.hours || "8.00")).toFixed(2)} Hours
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-amber-50 rounded-lg">
                            <Info className="h-4 w-4 text-amber-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Reason for Leave</p>
                            <p className="text-sm text-gray-700 font-medium mt-1 leading-relaxed">
                              {selectedRecord.details.reason || "No specific reason provided."}
                            </p>
                          </div>
                        </div>
                      </div>

                      {selectedRecord.details.attachments && selectedRecord.details.attachments.length > 0 && (
                        <div className="flex flex-col gap-2 pt-2 border-t border-gray-100">
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5 ml-0.5">
                            <FileText className="h-3 w-3" /> Attachments
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {selectedRecord.details.attachments.map((file, idx) => (
                              <a 
                                key={idx} 
                                href={file.url} 
                                target="_blank" 
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-bold text-blue-600 hover:border-blue-200 hover:bg-blue-50 transition-all shadow-sm"
                              >
                                <Paperclip className="h-3 w-3" />
                                {file.name}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      <LeaveHandoverSummary handover={selectedRecord.details.handover} variant="plain" />
                    </div>
                  </>
                )}

                {selectedRecord.details?.type === 'absence' && (
                  <>
                    <div className="flex items-center gap-3 text-gray-600">
                      <div className="p-2 bg-red-50 rounded-lg">
                        <AlertCircle className="h-4 w-4 text-red-600" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Reported Duration</p>
                        <p className="text-sm font-bold text-gray-900">{selectedRecord.details.hours} Hours</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 text-gray-600">
                      <div className="p-2 bg-gray-50 rounded-lg">
                        <FileText className="h-4 w-4 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Reporting Notes</p>
                        <p className="text-sm font-bold text-gray-900 italic">Reported as absentee</p>
                        {(selectedRecord.details.reason || selectedRecord.details.notes) && (
                          <p className="text-xs text-gray-500 mt-1">
                            {selectedRecord.details.reason && <span>{selectedRecord.details.reason}. </span>}
                            {selectedRecord.details.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {selectedRecord.details?.type === 'present' && (
                  <div className="flex items-center gap-3 text-gray-600">
                    <div className="p-2 bg-green-50 rounded-lg">
                      <Info className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Status Note</p>
                      <p className="text-sm font-bold text-gray-900">Marked as Present</p>
                    </div>
                  </div>
                )}

                {selectedRecord.reportedBy && (
                  <div className="flex items-center gap-3 text-gray-500 bg-gray-50/80 p-3 rounded-xl border border-dotted border-gray-200">
                    <HistoryIcon className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-[9px] font-black text-gray-400 uppercase tracking-[0.15em]">Admin / Manager Audit</p>
                      <p className="text-xs font-bold text-gray-700">
                        Authorized by <span className="text-blue-600">{selectedRecord.reportedBy}</span>
                      </p>
                      <p className="text-[10px] text-gray-400 font-medium">
                        On {formatDateTime(selectedRecord.reportedAt!)}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="pt-4">
                <Button 
                  onClick={() => setIsDetailModalOpen(false)}
                  className="w-full bg-black text-white hover:bg-gray-800 h-12 rounded-xl font-bold"
                >
                  Close Details
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttendanceHistory;
